import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill, spring } from "remotion";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: inter } = loadInter("normal", { weights: ["600", "700", "800"], subsets: ["latin"] });

const TempleLineSVG: React.FC = () => (
  <svg width="53" height="47" viewBox="0 0 110 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="100" width="100" height="8" rx="2" fill="rgba(255,215,0,0.6)" />
    <rect x="20" y="74" width="70" height="28" rx="2" fill="none" stroke="rgba(255,215,0,0.7)" strokeWidth="2" />
    <path d="M42 102 L42 84 Q55 74 68 84 L68 102Z" fill="rgba(255,215,0,0.3)" stroke="rgba(255,215,0,0.5)" strokeWidth="1.5" />
    <path d="M14 74 L96 74 L88 60 L22 60Z" fill="none" stroke="rgba(255,215,0,0.7)" strokeWidth="1.5" />
    <path d="M24 60 L86 60 L78 46 L32 46Z" fill="none" stroke="rgba(255,215,0,0.7)" strokeWidth="1.5" />
    <path d="M34 46 L76 46 L55 20Z" fill="none" stroke="rgba(255,215,0,0.7)" strokeWidth="1.5" />
    <circle cx="55" cy="10" r="5" fill="rgba(255,215,0,0.8)" />
    <line x1="55" y1="74" x2="55" y2="83" stroke="rgba(255,215,0,0.6)" strokeWidth="1.5" />
    <path d="M55 83 C55 83 48 85 47 93 L63 93 C62 85 55 83 55 83Z" fill="rgba(255,215,0,0.7)" stroke="rgba(255,215,0,0.4)" strokeWidth="1" />
  </svg>
);

const ThumbUpSVG: React.FC<{ color: string }> = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 64 64" fill="none">
    <path d="M20 28L28 8C30 8 34 9 34 16V24H52C54 24 56 26 55 30L50 52C49 54 47 55 45 55H20V28Z" fill={color} />
    <rect x="8" y="28" width="12" height="28" rx="3" fill={color} />
  </svg>
);

export const DevaKathaLS4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const barY = spring({ frame, fps, config: { damping: 16, stiffness: 100 }, durationInFrames: Math.round(0.8 * fps) });
  const translateY = interpolate(barY, [0, 1], [80, 0]);

  const slideDown = interpolate(frame, [Math.round(3.5 * fps), Math.round(4 * fps)], [0, 80], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.bezier(0.4, 0, 1, 1),
  });

  const bellAngle = interpolate(
    frame,
    [Math.round(0.2 * fps), Math.round(0.5 * fps), Math.round(0.8 * fps), Math.round(1.1 * fps), Math.round(1.4 * fps)],
    [0, 20, -15, 8, 0],
    { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
  );

  const likeClicked = frame >= Math.round(1.6 * fps);
  const subClicked = frame >= Math.round(2.4 * fps);

  const likeScale = interpolate(frame,
    [Math.round(1.6 * fps), Math.round(1.67 * fps), Math.round(1.8 * fps)],
    [1, 0.86, 1.07], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  const subScale = interpolate(frame,
    [Math.round(2.4 * fps), Math.round(2.47 * fps), Math.round(2.6 * fps)],
    [1, 0.86, 1.07], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        transform: `translateY(${translateY + slideDown}px)`,
        background: "linear-gradient(to right, rgba(10,8,5,0.95) 0%, rgba(20,15,5,0.92) 50%, rgba(10,8,5,0.95) 100%)",
        borderTop: "1px solid rgba(255,215,0,0.35)",
        padding: "9px 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ transform: `rotate(${bellAngle}deg)`, transformOrigin: "55px 74px" }}>
            <TempleLineSVG />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ color: "#FFD700", fontSize: 15, fontWeight: 800, fontFamily: inter, letterSpacing: 0.5 }}>Deva Katha</span>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9, fontWeight: 500, fontFamily: inter }}>& Mandir Yatra</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            background: likeClicked ? "#1a73e8" : "rgba(255,255,255,0.1)",
            border: `1px solid ${likeClicked ? "transparent" : "rgba(255,255,255,0.2)"}`,
            borderRadius: 17, padding: "7px 16px",
            transform: `scale(${likeScale})`,
            boxShadow: likeClicked ? "0 4px 16px rgba(26,115,232,0.5)" : "none",
          }}>
            <ThumbUpSVG color={likeClicked ? "#fff" : "rgba(255,255,255,0.85)"} />
            <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: inter }}>
              {likeClicked ? "Liked!" : "Like"}
            </span>
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            background: subClicked ? "#606060" : "#FF0000",
            borderRadius: 17, padding: "7px 19px",
            transform: `scale(${subScale})`,
            boxShadow: subClicked ? "none" : "0 4px 22px rgba(255,0,0,0.55)",
          }}>
            <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, fontFamily: inter }}>
              {subClicked ? "Subscribed ✓" : "Subscribe"}
            </span>
          </div>
        </div>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
